import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const OffersPanel = ({ offers, onAccept, onDecline, currentLanguage }) => {
  const translations = {
    en: {
      receivedOffers: 'Received Offers',
      noOffers: 'No offers yet',
      waitingOffers: 'Waiting for travelers to send offers',
      traveler: 'Traveler',
      proposedFee: 'Proposed Fee',
      rating: 'Rating',
      message: 'Message',
      accept: 'Accept',
      decline: 'Decline',
      verified: 'Verified',
      trips: 'trips'
    },
    ko: {
      receivedOffers: '받은 제안',
      noOffers: '아직 제안 없음',
      waitingOffers: '여행자의 제안을 기다리는 중',
      traveler: '여행자',
      proposedFee: '제안 수수료',
      rating: '평점',
      message: '메시지',
      accept: '수락',
      decline: '거절',
      verified: '인증됨',
      trips: '여행'
    }
  };

  const t = translations?.[currentLanguage];

  const formatCurrency = (amount) => {
    return `${amount?.toLocaleString()}원`;
  };

  if (offers?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6 md:p-8 lg:p-12 text-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Inbox" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
          {t?.noOffers}
        </h3>
        <p className="text-sm md:text-base text-muted-foreground">
          {t?.waitingOffers}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Mail" size={24} color="var(--color-accent)" />
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
            {t?.receivedOffers}
          </h2>
        </div>
      </div>
      <div className="divide-y divide-border">
        {offers?.map((offer) => (
          <div key={offer?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-base">
            <div className="flex flex-col md:flex-row gap-4 md:gap-6">
              <div className="flex items-start gap-3 flex-1">
                <div className="relative flex-shrink-0">
                  <Image
                    src={offer?.traveler?.avatarUrl || 'https://via.placeholder.com/64'}
                    alt={`${offer?.traveler?.fullName || 'Traveler'} profile picture`}
                    className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover"
                  />
                  {offer?.traveler?.isVerified && (
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 md:w-6 md:h-6 bg-success rounded-full border-2 border-card flex items-center justify-center">
                      <Icon name="Check" size={12} color="white" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-semibold text-foreground text-base md:text-lg">
                      {offer?.traveler?.fullName || 'Unknown Traveler'}
                    </h3>
                    {offer?.traveler?.isVerified && (
                      <span className="text-xs text-success caption">{t?.verified}</span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mb-2 text-sm">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} color="var(--color-warning)" />
                      <span className="font-medium data-text">{offer?.traveler?.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                    <span className="text-muted-foreground caption">
                      {offer?.traveler?.completedTrips || 0} {t?.trips}
                    </span>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-3 mb-3">
                    <p className="text-sm text-muted-foreground caption mb-1">{t?.message}</p>
                    <p className="text-sm text-foreground">{offer?.message}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Icon name="DollarSign" size={16} color="var(--color-primary)" />
                    <span className="text-sm text-muted-foreground caption">{t?.proposedFee}:</span>
                    <span className="text-base md:text-lg font-semibold text-primary data-text">
                      {formatCurrency(offer?.proposedFee)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex md:flex-col gap-2 md:gap-3">
                <Button
                  variant="default"
                  size="default"
                  iconName="Check"
                  iconPosition="left"
                  onClick={() => onAccept(offer?.id)}
                  fullWidth
                  className="md:w-32"
                >
                  {t?.accept}
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  iconName="X"
                  iconPosition="left"
                  onClick={() => onDecline(offer?.id)}
                  fullWidth
                  className="md:w-32"
                >
                  {t?.decline}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OffersPanel;